import { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2, Download, FileText } from "lucide-react";
import { LOGO_URL } from "@/config/branding";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking");
  const [notificationId, setNotificationId] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const pollStatus = useCallback(async () => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    try {
      const { data } = await axios.get(`${API}/payments/status/${sessionId}`, { withCredentials: true });
      setNotificationId(data.notification_id);

      if (data.payment_status === "paid") {
        setStatus("success");
      } else if (data.status === "expired") {
        setStatus("expired");
      } else {
        if (attempts < 10) {
          setTimeout(() => setAttempts((a) => a + 1), 2000);
        } else {
          setStatus("timeout");
        }
      }
    } catch {
      if (attempts < 5) {
        setTimeout(() => setAttempts((a) => a + 1), 2000);
      } else {
        setStatus("error");
      }
    }
  }, [sessionId, attempts]);

  useEffect(() => {
    pollStatus();
  }, [pollStatus]);

  const downloadCertificate = () => {
    if (notificationId) {
      window.open(`${API}/certificates/${notificationId}/download`, "_blank");
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex flex-col" data-testid="payment-success-page">
      {/* Logo Bar */}
      <div className="bg-white border-b-2 border-[#0A0A0A] px-6 py-3">
        <img src={LOGO_URL} alt="Self Certify UK" className="h-16 sm:h-20 object-contain object-left" />
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
      <div className="max-w-lg w-full">

        <div className="form-container text-center">
          {status === "checking" && (
            <div data-testid="payment-checking">
              <Loader2 className="w-16 h-16 text-[#D4A537] animate-spin mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Verifying Payment
              </h2>
              <p className="text-sm text-[#71717A]">
                Please wait while we confirm your payment...
              </p>
            </div>
          )}

          {status === "success" && (
            <div data-testid="payment-success">
              <div className="w-20 h-20 bg-[#D4A537] flex items-center justify-center border-2 border-[#0A0A0A] mx-auto mb-6" style={{ boxShadow: '4px 4px 0px #0A0A0A' }}>
                <CheckCircle2 className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Payment Successful
              </h2>
              <p className="text-sm text-[#71717A] mb-6">
                Your Building Notification certificate has been generated. It will be emailed to the local authority, homeowner, and your registered email.
              </p>

              <div className="space-y-3">
                <button
                  data-testid="download-certificate-button"
                  onClick={downloadCertificate}
                  className="brutalist-btn w-full flex items-center justify-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download Certificate (PDF)
                </button>
                <Link
                  to="/dashboard"
                  data-testid="new-notification-link"
                  className="brutalist-btn-outline w-full flex items-center justify-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  View Dashboard
                </Link>
              </div>
            </div>
          )}

          {(status === "error" || status === "expired" || status === "timeout") && (
            <div data-testid="payment-error">
              <div className="w-20 h-20 bg-[#FF3B30] flex items-center justify-center border-2 border-[#0A0A0A] mx-auto mb-6" style={{ boxShadow: '4px 4px 0px #0A0A0A' }}>
                <XCircle className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {status === "expired" ? "Payment Expired" : status === "timeout" ? "Verification Timeout" : "Payment Error"}
              </h2>
              <p className="text-sm text-[#71717A] mb-6">
                {status === "expired"
                  ? "Your payment session has expired. Please try again."
                  : status === "timeout"
                  ? "We couldn't confirm your payment in time. Please check your email for confirmation."
                  : "Something went wrong. Please try again or contact support."}
              </p>
              <Link
                to="/notification"
                data-testid="try-again-link"
                className="brutalist-btn w-full flex items-center justify-center gap-2"
              >
                Try Again
              </Link>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}
